import { QueryKey, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { LONG_POLLING_INTERVAL } from '../constants';
import { PeriodicRequest } from '../services/periodic-request';
import { isHttp2Available } from './utils';

/**
 * Subscribes to a data stream using SSE when HTTP/2 is available, or falls back to polling.
 * Returns a tuple of [unsubscribe, refresh]. refresh is a no-op in SSE mode.
 */
export function startSseOrPolling<T>(
  onReceived: (data: NonNullable<T>) => void,
  sse: (callback: (data: NonNullable<T>) => void) => () => void,
  poll: () => Promise<T | undefined>
): [unsubscribe: () => void, refresh: () => Promise<void>] {
  if (isHttp2Available()) {
    return [sse(onReceived), () => Promise.resolve()];
  }

  const periodicRequest = new PeriodicRequest<T | undefined>({
    interval: LONG_POLLING_INTERVAL,
    onPeriodicRequest: poll,
    onPeriodicRequestResult: onReceived,
  });
  return [periodicRequest.start(), periodicRequest.refresh];
}

export function useSseWithPollingFallback<T>({
  queryKey,
  subscribeSSE,
  fetchFallbackFn,
  refetchInterval,
}: {
  queryKey: QueryKey;
  subscribeSSE: () => () => void;
  fetchFallbackFn: () => Promise<T | undefined>;
  refetchInterval?: number;
}) {
  const http2 = isHttp2Available();

  useEffect(() => {
    if (!http2) {
      return;
    }
    return subscribeSSE();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [http2, JSON.stringify(queryKey)]);

  const { data } = useQuery<T>({
    queryKey,
    enabled: !http2, // False when we don't want to trigger a fetch here — SSE is the only source.
    refetchInterval,
    queryFn: async () => (await fetchFallbackFn()) as T,
  });

  return { data, isLoading: !data };
}
